import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { RecordActions } from '../../components/RecordActions';
import { RECORD_FIELDS } from '../../components/recordFields';
import { InspectionEditor } from './InspectionEditor';
import { Pill } from '../../components/ui';
import { dateLabel } from '../../lib/format';
import { openAuthenticatedPrint } from '../../lib/print';
import { api, ApiError } from '../../lib/api';
import { can } from '../../lib/permissions';
import { useAuth } from '../../lib/auth';
import type { InspectionResult, InspectionDisposition } from '../../lib/types';

// P1-7：不合格处置。核心库 quality_inspections.disposition 有 7 种取值，
// 此前只有 QC 服务令牌能写、控制台完全不展示 —— 现场判定的「返工/让步/报废」在办公室是看不见的死数据。
// 展示口径与写入口径必须一致，因此枚举直接来自 types.ts，不在此处另写一份。
const DISPOSITION_LABEL: Record<InspectionDisposition, { label: string; tone: 'ok' | 'bad' | 'warn' | 'neutral' }> = {
  rework: { label: '返工', tone: 'warn' },
  re_clad: { label: '重新熔覆', tone: 'warn' },
  re_machine: { label: '重新加工', tone: 'warn' },
  scrap: { label: '报废', tone: 'bad' },
  concession: { label: '让步接收', tone: 'neutral' },
  return_to_supplier: { label: '退回供应商', tone: 'bad' },
  return_original: { label: '无法修复·原件退回', tone: 'bad' },
};

export type QualityInspectionRow = {
  id: string;
  number: string;
  inspection_date?: string;
  inspector_name?: string;
  item_code?: string;
  item_name_zh?: string;
  item_name_es?: string;
  work_order_number?: string;
  contract_number?: string;
  serial_number?: string;
  result: InspectionResult;
  source_system?: string;
  stages?: Array<{
    stage_name: string;
    result: string;
    operator_name?: string;
    readings?: Record<string, unknown> | null;
    photos?: string[];
    notes?: string;
  }>;
  report_rows?: Array<Record<string, unknown>>;
  remarks_zh?: string;
  remarks_es?: string;
  inspection_kind?: string;
  process_type?: string;
  delivery_number?: string;
  source_id?: string;
  // P1-7
  disposition?: InspectionDisposition | null;
  disposition_notes?: string | null;
  disposition_at?: string | null;
  inspected_by_name?: string | null;
  asset_id?: string | null;
  service_cycle_id?: string | null;
  contract_line_id?: string | null;
  sync_state?: string | null;
  revision?: number | null;
  rolled_up_at?: string | null;
  identity_verified_at?: string | null;
};

function formatAcceptance(r: Record<string, unknown>): string {
  // ERPNext value 原文优先（>40 / ±0.05 / <15）
  const raw = r.criterion_text ?? r.value;
  if (raw != null && raw !== '') {
    const s = String(raw);
    if (!/[一-鿿]/.test(s)) return s;
  }
  const unit = (r.unit as string) || '';
  const u = unit ? ` ${unit}` : '';
  const num = (v: unknown) => (v === null || v === undefined || v === '' ? null : Number(v));
  const min = num(r.min_value);
  const max = num(r.max_value);
  const hasRange = min != null && max != null && !(min === 0 && max === 0);
  if (hasRange) return `${min} – ${max}${u}`;
  if (min != null && min !== 0) return `≥ ${min}${u}`;
  if (max != null && max !== 0) return `≤ ${max}${u}`;
  const cmp = String(r.comparator || '');
  const tgt = num(r.target_value);
  if (cmp === '>=' && tgt != null) return `> ${tgt}${u}`;
  if (cmp === '<=' && tgt != null) return `< ${tgt}${u}`;
  if (cmp === 'tolerance') {
    const tol = r.tolerance;
    if (tol != null && tol !== '') return `±${tol}${u}`;
  }
  if (cmp && tgt != null) return `${cmp} ${tgt}${u}`;
  return '—';
}

function resultPill(r: InspectionResult) {
  // 质检单只记数据、不判合格；结论字段仅系统内部，不对客户判定
  const map: Record<InspectionResult, { tone: 'ok' | 'bad' | 'warn' | 'neutral'; label: string }> = {
    accepted: { tone: 'neutral', label: '已记录' },
    rejected: { tone: 'neutral', label: '已记录' },
    conditional: { tone: 'neutral', label: '已记录' },
    pending: { tone: 'ok', label: '已记录' },
  };
  const s = map[r] || map.pending;
  return <Pill tone={s.tone} label={s.label} />;
}

export function InspectionsPage() {
  const [sp] = useSearchParams();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [showEditor, setShowEditor] = useState(false);

  const columns: Column<QualityInspectionRow>[] = [
    { header: '质检单号', render: (i) => <span className="num strong">{i.number}</span> },
    { header: '检验日期', render: (i) => <span className="num">{dateLabel(i.inspection_date)}</span> },
    { header: '检验人', render: (i) => i.inspector_name || '—' },
    { header: '物料', render: (i) => <span className="muted">{i.item_code || '—'} {i.item_name_zh || i.item_name_es || ''}</span> },
    { header: '关联工单', render: (i) => <span className="num muted">{i.work_order_number || '—'}</span> },
    { header: '关联合同', render: (i) => <span className="num muted">{i.contract_number || '—'}</span> },
    { header: '关联交货', render: (i) => <span className="num muted">{(i as { delivery_number?: string }).delivery_number || '—'}</span> },
    { header: '序列号', render: (i) => <span className="num muted">{i.serial_number || '—'}</span> },
    { header: '数据', render: (i) => resultPill(i.result) },
  ];

  return (
    <>
      {showEditor ? (
        <InspectionEditor onDone={() => { setShowEditor(false); setReloadSignal((n) => n + 1); }} />
      ) : null}
      <ListPage<QualityInspectionRow>
        title="质检单"
        desc="正式质检表与西语单统一使用 Portal/ERPNext 检验模板；QC 全量过程数据保留在来源系统。"
        endpoint="/api/inspections"
        columns={columns}
        rowId={(i) => i.id}
        searchPlaceholder="搜索质检单号 / 检验人 / 序列号 / 工单 / 合同…"
        initialQuery={sp.get('q') || ''}
        reloadSignal={reloadSignal}
        rowActions={(row) => <RecordActions type="inspections" row={row} fields={RECORD_FIELDS['inspections']} label={String(row.number)} onDone={() => setReloadSignal((n) => n + 1)} />}
        renderDetail={(row) => (
          <InspectionDetailBody row={row} onChanged={() => setReloadSignal((n) => n + 1)} />
        )}
      />
      {!showEditor ? (
        <div style={{ margin: '8px 0' }}>
          <button className="btn primary" onClick={() => setShowEditor(true)}>+ 新建质检单（录入数据）</button>
        </div>
      ) : null}
    </>
  );
}

function InspectionDetailBody({ row, onChanged }: { row: QualityInspectionRow; onChanged: () => void }) {
  const [printing, setPrinting] = useState(false);
  const [printErr, setPrintErr] = useState<string | null>(null);
  const [detail, setDetail] = useState<QualityInspectionRow>(row);
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const stages = detail.stages || [];
  const reportRows = detail.report_rows || [];

  // 列表接口刻意保持轻量；展开时必须读取详情接口，否则阶段、测量值和照片
  // 永远是空的，看起来像质检数据丢失。打印接口也使用同一详情投影。
  useEffect(() => {
    let active = true;
    setDetail(row);
    setDetailErr(null);
    api.detail<QualityInspectionRow>(`/api/inspections/${encodeURIComponent(row.id)}`)
      .then((data) => { if (active) setDetail(data); })
      .catch((e) => { if (active) setDetailErr(e instanceof ApiError ? e.message : '质检详情读取失败'); });
    return () => { active = false; };
  }, [row]);

  // 打印接口需要 Authorization 头，普通 <a href> 新开标签页带不上 token（会 401 未登录），
  // 必须走 openAuthenticatedPrint 的带鉴权 fetch。
  const doPrint = async () => {
    setPrinting(true); setPrintErr(null);
    const r = await openAuthenticatedPrint('inspection', row.id, { format: 'pdf' });
    if (!r.ok) setPrintErr(r.message || '打印失败');
    setPrinting(false);
  };
  return (
    <div style={{ padding: '8px 4px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10, margin: '8px 0' }}>
        <Field label="物料" value={`${detail.item_code || '—'} ${detail.item_name_zh || detail.item_name_es || ''}`} />
        <Field label="序列号" value={detail.serial_number || '—'} />
        <Field label="关联订单" value={detail.contract_number || '—'} />
        <Field label="关联工单" value={detail.work_order_number || '—'} />
        <Field label="关联交货单" value={detail.delivery_number || '—'} />
        <Field label="检验节点" value={detail.inspection_kind || detail.process_type || '—'} />
        <Field label="来源" value={detail.source_system || '系统内建'} />
        <Field label="业务键" value={detail.source_id || '—'} />
        <Field label="身份核验" value={row.identity_verified_at ? `已核验 ${dateLabel(row.identity_verified_at)}` : '未核验'} />
        <Field label="同步状态" value={row.sync_state || '—'} />
        <Field label="版本" value={row.revision != null ? `r${row.revision}` : '—'} />
      </div>

      {detailErr ? <div style={{ color: '#dc2626', fontSize: 13, margin: '8px 0' }}>{detailErr}</div> : null}
      {reportRows.length > 0 ? (
        <div style={{ marginTop: 10, overflowX: 'auto' }}>
          <div className="muted" style={{ fontSize: 12, marginBottom: 6 }}>
            Portal/ERPNext 正式检验模板（项目名称、顺序与允收指数保持一致）
          </div>
          <table style={{ width: '100%', minWidth: 640, borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                <th style={{ ...th, width: '34%' }}>Parámetro</th>
                <th style={{ ...th, width: '18%' }}>Valor</th>
                <th style={{ ...th, width: '16%' }}>Lectura 1</th>
                <th style={{ ...th, width: '16%' }}>Lectura 2</th>
                <th style={{ ...th, width: '16%' }}>Lectura 3</th>
              </tr>
            </thead>
            <tbody>
              {reportRows.map((r, i) => {
                const vals = Array.isArray(r.readings) ? r.readings : [];
                return (
                  <tr key={String(r.source_template_stage_id || r.key || i)} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={td}>{String(r.label_es || '—')}</td>
                    <td style={td}>{formatAcceptance(r)}</td>
                    <td style={td}>{vals[0] != null && vals[0] !== '' ? String(vals[0]) : '—'}</td>
                    <td style={td}>{vals[1] != null && vals[1] !== '' ? String(vals[1]) : '—'}</td>
                    <td style={td}>{vals[2] != null && vals[2] !== '' ? String(vals[2]) : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {stages.some((s) => (s.photos || []).length) ? (
            <div style={{ marginTop: 8, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {stages.flatMap((s) => s.photos || []).map((p, i) => (
                <img key={i} src={p} alt="foto" style={{ maxWidth: 80, maxHeight: 60, objectFit: 'cover', borderRadius: 4 }} />
              ))}
            </div>
          ) : null}
        </div>
      ) : <div style={{ color: '#94a3b8', fontSize: 13, margin: '8px 0' }}>未关联 Portal/ERPNext 检验模板，暂无正式检验项目</div>}

      <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <button className="btn" onClick={doPrint} disabled={printing}>{printing ? '生成中…' : '打印西语质检单 PDF'}</button>
        <button className="btn" onClick={onChanged}>刷新</button>
        {printErr ? <span style={{ color: '#dc2626', fontSize: 13 }}>{printErr}</span> : null}
      </div>
      <DispositionPanel row={row} onChanged={onChanged} />
      <div style={{ marginTop: 6, color: '#94a3b8', fontSize: 12 }}>
        只记数据、不判合格（超允收客户仍可能接受）· 与手机端同一记录 · 出西语单按 ERPNext 出单模板抽检验项目。
      </div>
    </div>
  );
}

// P1-7 不合格处置面板。
// 规则与核心库一致：仅 rejected / conditional 可处置；维修受托保管件禁止直接报废（核心库 409 拦截）。
// 已处置的单不允许在此改判 —— 改判要走核心库留痕流程，界面上不给这个口子。
function DispositionPanel({ row, onChanged }: { row: QualityInspectionRow; onChanged: () => void }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<InspectionDisposition>('rework');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const allowed = can(user, 'inspections', 'edit');
  const eligible = row.result === 'rejected' || row.result === 'conditional';
  const already = !!row.disposition;

  if (!allowed || already || !eligible) {
    if (already) {
      return (
        <div style={{ marginTop: 12, padding: 10, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
          <strong>不合格处置：</strong>
          {DISPOSITION_LABEL[row.disposition as InspectionDisposition]?.label || row.disposition}
          {row.disposition_notes ? ` · ${row.disposition_notes}` : ''}
          {row.disposition_at ? ` · ${dateLabel(row.disposition_at)}` : ''}
        </div>
      );
    }
    return null;
  }

  const submit = async () => {
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/inspections/${row.id}/disposition`, { disposition: value, disposition_notes: notes || null });
      setOpen(false); setNotes('');
      onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '处置提交失败');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 12, padding: 10, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 6, fontSize: 13 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <strong>不合格处置</strong>
        {!open ? (
          <button className="btn" onClick={() => setOpen(true)}>登记处置</button>
        ) : (
          <>
            <select className="input" value={value} onChange={(e) => setValue(e.target.value as InspectionDisposition)}>
              {(Object.keys(DISPOSITION_LABEL) as InspectionDisposition[]).map((k) => (
                <option key={k} value={k}>{DISPOSITION_LABEL[k].label}</option>
              ))}
            </select>
            <input
              className="input" placeholder="处置备注（原因 / 客户沟通结论）" value={notes}
              onChange={(e) => setNotes(e.target.value)} style={{ minWidth: 260 }}
            />
            <button className="btn" onClick={submit} disabled={busy}>{busy ? '提交中…' : '确认'}</button>
            <button className="btn" onClick={() => setOpen(false)} disabled={busy}>取消</button>
          </>
        )}
      </div>
      {err ? <div style={{ color: '#dc2626', marginTop: 6 }}>{err}</div> : null}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div style={{ color: '#94a3b8', fontSize: 11 }}>{label}</div>
      <div style={{ fontSize: 13, wordBreak: 'break-all' }}>{value}</div>
    </div>
  );
}

const th: React.CSSProperties = { padding: '6px 8px', textAlign: 'left', fontWeight: 600 };
const td: React.CSSProperties = { padding: '6px 8px', verticalAlign: 'top' };
